import React, { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import StatusPill from "@/components/StatusPill";
import ProgressRing from "@/components/ProgressRing";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";

const StatCard = ({ label, value, accent = "#1B1E23", testId, sub }) => (
  <div className="paper-card p-6" data-testid={testId}>
    <div className="mono-label">{label}</div>
    <div className="mt-2 font-archivo font-bold text-4xl leading-none" style={{ color: accent }}>{value}</div>
    {sub && <div className="mono-label mt-3" style={{opacity: 1}}>{sub}</div>}
  </div>
);

function formatDuration(hours) {
  if (hours == null) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const t = useT();
  const [stats, setStats] = useState({});
  const [projekte, setProjekte] = useState([]);
  const [, setChatMsgs] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, p, c] = await Promise.all([
        api.get("/dashboard"),
        api.get("/projekte"),
        api.get("/chat/unread").catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setProjekte(p.data);
      setChatMsgs(c.data || []);
    })();
  }, []);

  const role = user?.rolle;
  const roleColor = role === "pm" ? "#0E62C4" : role === "designer" ? "#BE2E6C" : "#1B1E23";
  const canReview = role === "admin" || role === "pm";
  const actionRequired = projekte.filter((p) => p.status === "wartet_auf_freigabe");

  return (
    <div>
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="mono-label" style={{ color: roleColor, opacity: 1 }}>{role} · {t("dashboard.overview")}</div>
          <h1 className="font-archivo font-bold text-4xl mt-1">{t("dashboard.greeting")}, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <div className="mono-label">{fmtDate(new Date().toISOString())}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-10">
        <StatCard label={t("dashboard.projekte_gesamt")} value={stats.projekte_gesamt ?? "—"} testId="stat-total" />
        <StatCard label={t("dashboard.in_arbeit")} value={stats.in_arbeit ?? "—"} accent="#0E62C4" testId="stat-inarbeit" />
        <StatCard label={canReview ? t("dashboard.wartet_freigabe") : t("dashboard.in_abstimmung")} value={canReview ? actionRequired.length : (stats.in_abstimmung ?? "—")} accent="#8A5A00" testId="stat-abstimmung" />
        {role === "designer" ? (
          <>
            <StatCard label={t("dashboard.stunden_monat")} value={stats.stunden_monat ?? "—"} accent="#BE2E6C" testId="stat-monat" />
            <StatCard label={t("dashboard.abgeschlossen")} value={stats.abgeschlossen ?? "—"} accent="#157A4A" testId="stat-done" />
          </>
        ) : (
          <>
            <StatCard label={t("dashboard.abgeschlossen")} value={stats.abgeschlossen ?? "—"} accent="#157A4A" testId="stat-done" />
            <StatCard
              label={t("dashboard.avg_freigabezeit")}
              value={stats.avg_freigabezeit_stunden != null ? formatDuration(stats.avg_freigabezeit_stunden) : "—"}
              accent="#0E62C4"
              testId="stat-avg-freigabe"
              sub={stats.freigabe_zaehler ? `Ø ${stats.freigabe_zaehler}` : "—"}
            />
          </>
        )}
      </div>

      {canReview && actionRequired.length > 0 && (
        <div className="paper-card p-6 mb-6 border-l-4" style={{ borderLeftColor: "#8A5A00" }} data-testid="pm-action-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle size={16} style={{ color: "#8A5A00" }} />
            <h2 className="font-archivo font-bold text-lg">{t("dashboard.action_required")}</h2>
            <span className="mono-label" style={{ color: "#8A5A00", opacity: 1 }}>{actionRequired.length} {t("dashboard.entwuerfe_warten")}</span>
          </div>
          <div className="space-y-2">
            {actionRequired.map((p) => (
              <Link
                key={p.id}
                to={`/projekte/${p.id}?tab=entwuerfe`}
                data-testid={`action-project-${p.id}`}
                className="flex items-center justify-between px-4 py-3 border border-line rounded-sm hover:border-ink transition-colors bg-white"
              >
                <div className="flex items-center gap-4">
                  <ProgressRing pct={p.fortschritt_pct} size={36} stroke={3} testId={`ring-action-${p.id}`} />
                  <div>
                    <div className="font-medium text-sm">{p.titel}</div>
                    <div className="mono-label mt-0.5">{p.kunde_name} · {p.designer_name}</div>
                  </div>
                </div>
                <StatusPill status={p.status} />
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="paper-card p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-archivo font-bold text-xl">{t("dashboard.active_projects")}</h2>
          <Link to="/projekte" className="mono-label hover:opacity-100" data-testid="dashboard-all-projects">{t("dashboard.see_all")}</Link>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="w-14"></th>
                <th className="text-left mono-label py-3 font-medium">{t("col.title")}</th>
                <th className="text-left mono-label py-3 font-medium">{t("col.client")}</th>
                <th className="text-left mono-label py-3 font-medium">{t("col.area")}</th>
                <th className="text-left mono-label py-3 font-medium">{t("col.deadline")}</th>
                <th className="text-left mono-label py-3 font-medium">{t("col.status")}</th>
              </tr>
            </thead>
            <tbody>
              {projekte.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-ink/50">{t("dashboard.no_projects")}</td></tr>
              )}
              {projekte.map((p) => (
                <tr key={p.id} className="border-b border-line/60 hover:bg-paper/60">
                  <td className="py-3">
                    <ProgressRing pct={p.fortschritt_pct} size={40} stroke={4} testId={`ring-${p.id}`} />
                  </td>
                  <td className="py-3">
                    <Link to={`/projekte/${p.id}`} className="font-medium hover:underline" data-testid={`row-project-${p.id}`}>{p.titel}</Link>
                  </td>
                  <td className="py-3">{p.kunde_name}</td>
                  <td className="py-3"><span className="mono-label" style={{opacity: 1}}>{p.bereich}</span></td>
                  <td className="py-3 font-mono text-xs">{p.laufend ? t("project.deadline_laufend") : fmtDate(p.deadline)}</td>
                  <td className="py-3"><StatusPill status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
